import axios from "axios";
import AuthenticationFailureHandler from "../utils/AuthenticationFailureHandler";
import { MESSAGES_BASE_URL, RESERVATION_BASE_URL } from "../utils/EmpUtil";
import { is401 } from "../errors/ErrorConstants";

export default class CustomerReservationsClient {
  static baseUrl = RESERVATION_BASE_URL;

  static messagesUrl = MESSAGES_BASE_URL;

  // New Messages Service Order's call
  static async getCustomerOrdersV2(userCommonId, filters = {}, pageParam = 0) {
    try {
      const commonId = decodeURIComponent(userCommonId);
      const commonIdMarket = commonId.split("|")[0];
      const requestMarket = commonIdMarket === "CAN" ? "CAN" : "USA";
      const encodedCommonId = encodeURIComponent(commonId);
      const API_URL = `${this.messagesUrl}/orders?dealerId=${encodedCommonId}&market=${encodeURIComponent(requestMarket)}&page=${pageParam}`;

      const requestBody = {
        actionRequired:
          filters.actionRequired === undefined ? null : filters.actionRequired,
        orderType: filters.orderRequestType || [],
        vehicleStatus: filters.vehicleStatus || [],
        orderStatus: filters.orderStatus || [],
      };

      const response = await axios.post(API_URL, requestBody);
      return response.data;
    } catch (error) {
      if (is401(error)) {
        AuthenticationFailureHandler.execute();
      }
      throw error;
    }
  }

  static getCustomerOrders(dealerCommonId, tab, lang, pageNumber, sort) {
    dealerCommonId = dealerCommonId.split("%7C").join("|");
    const url = `${this.baseUrl}/dealer/customer-reservations`;

    return axios
      .post(url, {
        commonIds: dealerCommonId,
        currentPage: pageNumber,
        sort: sort,
        lang: lang,
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }

        throw error;
      });
  }

  static updateHandOver(handOverData, orderId) {
    const url = `${this.baseUrl}/dealer/customer-reservations/handover-update/${orderId}`;

    return axios
      .post(url, handOverData)
      .then(() => {})
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        throw error;
      });
  }

  static getCancelOrderReasons(lang, type, commonId = null) {
    let url = `${this.baseUrl}/dealer/cancelOrderReasons?lang=${lang}`;

    if (type === "all") {
      url = url + `&purchaseRequest=false`;
    }

    if (type === "purchaseRequest") {
      url = url + `&purchaseRequest=true`;
    }

    if (type === "consentReasons") {
      url = url + `&consentReasons=true`;
    }

    if (type === "reservationReasons") {
      url = url + `&reservationReasons=true`;
    }
    if (type === "vehicleHoldReasons") {
      url = url + `&vehicleHoldReasons=true`;
    }

    if (commonId) {
      const dealerCommonId = commonId.split("|").join("%7C");
      url = url + `&commonId=${dealerCommonId}`;
    }

    return axios.get(url);
  }

  static getWindowSticker(paCode, vin) {
    const url = `${this.baseUrl}/dealer/window-sticker?dealerPACode=${paCode}`;

    const headers = {
      headers: {
        ...(vin && { vin }),
      },
    };

    return axios.get(url, headers);
  }

  static async fetchOrderDetails(orderId, commonId, isCrc, lang, market) {
    let url = `${this.baseUrl}/dealer/customer-reservations/${orderId}?lang=${lang}`;
    url = url + (isCrc ? "&market=" + market : "&dealerId=" + commonId);
    return await axios.get(encodeURI(url));
  }

  static async getFinanceLoanAppDetails(orderId, loanAppId) {
    let url = `${this.baseUrl}/dealer/customer-reservations/loan-app-details?orderId=${orderId}&loanAppId=${loanAppId}`;
    return await axios
      .get(encodeURI(url))
      .then((response) => response.data)
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        throw error;
      });
  }

  static async getPricingStack(orderId, market, commonId, lang) {
    const dealerCommonId = commonId ? commonId.split("%7C").join("|") : "";

    let url = `${
      this.baseUrl
    }/dealer/customer-reservations-pricing/${orderId}?lang=${lang ? "en" : ""}`;
    url = commonId ? url + ("&dealerId=" + dealerCommonId) : url;
    url = market ? url + ("&market=" + market) : url;
    return await axios.get(encodeURI(url));
  }

  static async getOrderPricingStack(user, orderId, market, commonId, lang) {
    const dealerCommonId = commonId ? commonId.split("%7C").join("|") : "";

    let url = `${
      this.baseUrl
    }/dealer/customer-reservations-pricing/${orderId}?lang=${lang ? "en" : ""}`;
    url = commonId ? url + ("&dealerId=" + dealerCommonId) : url;
    url = market ? url + ("&market=" + market) : url;
    return await axios
      .get(encodeURI(url))
      .then((response) => response.data)
      .catch(async (error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        throw error;
      });
  }

  static async searchCustomerReservations(
    searchQuery,
    user,
    lang,
    commonIds,
    market,
    ...searchParams
  ) {
    const [lastName = null, zipCode = null, isLastNameSearch] = searchParams;
    let url;

    if (lastName) {
      if (zipCode) {
        url = `${this.baseUrl}/dealer/customer-reservations/search?lastName=${lastName}&zipCode=${zipCode}&lang=${lang}`;
      } else {
        url = `${this.baseUrl}/dealer/customer-reservations/search?lastName=${lastName}&lang=${lang}`;
      }
    } else if (isLastNameSearch) {
      url = `${this.baseUrl}/dealer/customer-reservations/search?isLastNameSearch=${isLastNameSearch}&lang=${lang}`;
    } else {
      url = `${this.baseUrl}/dealer/customer-reservations/search?lang=${lang}`;
    }

    if (market) {
      url = url + `&market=${market}`;
    }

    const headers = {
      headers: {
        ...(searchQuery && { searchQuery }),
        ...(commonIds && { commonIds }),
      },
    };

    return axios
      .get(url, headers)
      .then((response) => response.data)
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        throw error;
      });
  }

  static async saveValuation(valuationRequest) {
    const url = `${this.baseUrl}/dealer/customer-reservations/trade-in`;

    return axios
      .put(url, {
        expiryDate: valuationRequest.expiryDate,
        valuationAmount: valuationRequest.valuationAmount,
        appraisalId: valuationRequest.appraisalId,
        orderId: valuationRequest.orderId,
      })
      .then((response) => response.data)
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        throw error;
      });
  }

  static async sendToUVIS(uvisRequest) {
    const url = `${this.baseUrl}/dealer/customer-reservations/uvis`;
    return axios.post(url, uvisRequest);
  }
}
